import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

/**
 * Service for managing loading states across the application.
 * Uses a key-based system to track multiple loading operations simultaneously.
 */
@Injectable({
  providedIn: 'root'
})
export class LoadingService {
  private readonly loadingSubject = new BehaviorSubject<boolean>(false);
  private readonly loadingMap = new Map<string, boolean>();
  private readonly debugIntervalId: any;

  /**
   * Observable that emits the current loading state.
   * True if any loading operation is in progress, false otherwise.
   */
  loading$ = this.loadingSubject.asObservable();

  constructor() {
    console.debug('LoadingService: Initializing debug interval');
    this.debugIntervalId = setInterval(() => {
      const loadingKeys = Array.from(this.loadingMap.keys());
      if (loadingKeys.length > 0) {
        console.debug('LoadingService: Current loading operations:', loadingKeys);
      }
    }, 5000);
  }

  /**
   * Indicates that a loading operation has started.
   * @param key A unique identifier for the loading operation. Use consistent keys across show/hide calls.
   * Example keys: 'userProfile', 'projects', 'data-fetch'
   *
   * Usage:
   * ```typescript
   * // Start loading
   * this.loadingService.show('userProfile');
   *
   * // In your template
   * <mat-spinner *ngIf="loadingService.loading$ | async"></mat-spinner>
   * ```
   */
  show(key?: string) {
    console.debug('LoadingService: Showing loader', key ? `for key: ${key}` : 'without key');
    if (key) {
      this.loadingMap.set(key, true);
    }
    this.updateLoadingState();
  }

  /**
   * Indicates that a loading operation has completed.
   * @param key The unique identifier for the loading operation that was started with show().
   * Must match the key used in the corresponding show() call.
   *
   * Usage:
   * ```typescript
   * // In your component
   * this.someService.getData().subscribe({
   *   next: (data) => {
   *     // Process data
   *     this.loadingService.hide('userProfile');
   *   },
   *   error: (error) => {
   *     // Handle error
   *     this.loadingService.hide('userProfile');
   *   }
   * });
   * ```
   */
  hide(key?: string) {
    console.debug('LoadingService: Hiding loader', key ? `for key: ${key}` : 'without key');
    if (key) {
      this.loadingMap.delete(key);
    }
    this.updateLoadingState();
  }

  /**
   * Updates the loading state based on whether any loading operations are in progress.
   * @private
   */
  private updateLoadingState() {
    const isLoading = this.loadingMap.size > 0;
    console.debug(`LoadingService: Updating loading state to ${isLoading}. Active loaders: ${this.loadingMap.size}`);
    this.loadingSubject.next(isLoading);
  }
}
